import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { toast } from 'sonner';
import { shouldUseDemoMode } from '../utils/demoMode';

// Use ReturnType<typeof setTimeout> instead of NodeJS.Timeout
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

interface Message {
  _id: string;
  chatId?: string;
  senderId: string;
  content: string;
  messageType: 'text' | 'image' | 'file';
  fileUrl?: string;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
  sender?: any;
}

interface Chat {
  _id: string;
  participants: any[];
  messages: Message[];
  lastMessage?: {
    content: string;
    senderId: string;
    timestamp: Date;
  };
  unreadCount: Map<string, number>;
  isActive: boolean;
}

export const useChat = (userId: string | null) => {
  const { socket, isConnected } = useSocket();
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [typing, setTyping] = useState<{ [chatId: string]: string[] }>({});
  const [loading, setLoading] = useState(false);
  const typingTimeoutRef = useRef<{ [chatId: string]: ReturnType<typeof setTimeout> }>({});

  // Fetch chats
  const fetchChats = useCallback(async () => {
    if (!userId || shouldUseDemoMode()) {
      return;
    }

    setLoading(true);
    try {
      // In real implementation, fetch from API
      // For now, return empty array
      setChats([]);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Join chat
  const joinChat = useCallback((chatId: string) => {
    if (socket && isConnected) {
      socket.emit('join_chat', chatId);
    }
  }, [socket, isConnected]);

  // Leave chat
  const leaveChat = useCallback((chatId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_chat', chatId);
    }
  }, [socket, isConnected]);

  // Send message
  const sendMessage = useCallback((
    chatId: string, 
    content: string, 
    messageType: 'text' | 'image' | 'file' = 'text', 
    fileUrl?: string
  ) => {
    if (socket && isConnected) {
      socket.emit('send_message', {
        chatId,
        content,
        messageType,
        fileUrl
      });
    }
  }, [socket, isConnected]);

  // Start typing
  const startTyping = useCallback((chatId: string) => {
    if (socket && isConnected) {
      socket.emit('typing_start', chatId);
    }
  }, [socket, isConnected]);

  // Stop typing
  const stopTyping = useCallback((chatId: string) => {
    if (socket && isConnected) {
      socket.emit('typing_stop', chatId);
    }
  }, [socket, isConnected]);

  // Handle typing with auto-stop
  const handleTyping = useCallback((chatId: string) => {
    startTyping(chatId);
    
    // Clear existing timeout
    if (typingTimeoutRef.current[chatId]) {
      clearTimeout(typingTimeoutRef.current[chatId]);
    }
    
    // Set new timeout to stop typing after 3 seconds
    typingTimeoutRef.current[chatId] = setTimeout(() => {
      stopTyping(chatId);
    }, 3000);
  }, [startTyping, stopTyping]);

  // Mark as read
  const markAsRead = useCallback((chatId: string) => {
    if (socket && isConnected) {
      socket.emit('mark_read', chatId);
    }
  }, [socket, isConnected]);

  // Delete message
  const deleteMessage = useCallback((chatId: string, messageId: string) => {
    if (socket && isConnected) {
      socket.emit('delete_message', { chatId, messageId });
    }
  }, [socket, isConnected]);

  // Get or create chat
  const getOrCreateChat = useCallback(async (otherUserId: string) => {
    if (shouldUseDemoMode()) {
      return {};
    }
    
    try {
      // In real implementation, call API
      return {};
    } catch (error) {
      return {};
    }
  }, []);

  // Check if user is online
  const isUserOnline = useCallback((userId: string) => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  // Setup socket listeners
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Listen for new messages
    socket.on('new_message', (message: Message) => {
      if (activeChat && message.chatId === activeChat._id) {
        setActiveChat(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [...prev.messages, message]
          };
        });
      }
    });

    // Listen for typing events
    socket.on('user_typing', (data: { chatId: string; userId: string }) => {
      setTyping(prev => ({
        ...prev,
        [data.chatId]: [...(prev[data.chatId] || []), data.userId]
      }));
    });

    socket.on('user_stop_typing', (data: { chatId: string; userId: string }) => {
      setTyping(prev => ({
        ...prev,
        [data.chatId]: (prev[data.chatId] || []).filter(id => id !== data.userId)
      }));
    });

    // Listen for online users
    socket.on('online_users', (users: string[]) => {
      setOnlineUsers(users);
    });

    return () => {
      socket.off('new_message');
      socket.off('user_typing');
      socket.off('user_stop_typing');
      socket.off('online_users');
    };
  }, [socket, isConnected, activeChat]);

  // Fetch chats on mount
  useEffect(() => {
    if (userId && !shouldUseDemoMode()) {
      fetchChats();
    }
  }, [userId, fetchChats]);

  return {
    socket,
    connected: isConnected,
    chats,
    activeChat,
    onlineUsers,
    typing,
    loading,
    fetchChats,
    setActiveChat,
    joinChat,
    leaveChat,
    sendMessage,
    startTyping,
    stopTyping,
    handleTyping,
    markAsRead,
    deleteMessage,
    getOrCreateChat,
    isUserOnline
  };
};
